/**
 * Utility functions for subscription handling
 */
import { generateStandardCertId } from '@/utils/cert-utils';
import { getPriceIdFromEnv, getProductPrefix } from '@/utils/env-helper';

/**
 * Determines if a product or cart item is a subscription
 * @param {Object} item - Product or cart item
 * @returns {boolean} True if item is a subscription
 */
export const isSubscriptionProduct = (item) => {
  if (!item) return false;

  // CRITICAL: ALL products are subscription-based, with no exceptions
  // The previous implementation had a bug where some products weren't properly detected
  // as subscriptions, resulting in payment intents instead of setup intents.

  // For debugging/logging purposes, still check the original conditions
  const isSubscriptionType = [
    'verified-mark-certificate',
    'government-mark-certificate',
    'common-mark-certificate',
    'modified-registered-mark',
    'dmarc-plus',
    'dmarc-premium',
  ].includes(item.id);

  // Also check for explicit subscription flag as a fallback
  const hasSubscriptionFlag = item.isSubscription === true;

  // Check for DMARC products by type
  const isDmarcProduct = item.type === 'dmarc';

  // Enhanced logging to diagnose subscription detection issues
  console.log('Subscription check in checkout:', {
    itemId: item.id,
    itemType: item.type,
    isSubscriptionType,
    hasSubscriptionFlag,
    isDmarcProduct,
    finalIsSubscription: true, // Always true - all products are subscriptions
  });

  // IMPORTANT: Always return true as ALL products are subscription-based
  return true;
};

/**
 * Gets subscription price ID for a given product
 * @param {Object} item - Product object
 * @returns {string|null} Price ID or null if not found
 */
export const getSubscriptionPriceId = (item) => {
  if (!isSubscriptionProduct(item)) return null;

  // Enhanced logging
  console.log(
    `Getting subscription price ID for ${item.id} with period ${item.options?.year || 1}`
  );

  // First check if price ID is directly provided in the item
  if (item.priceId) {
    console.log(`Using product-provided price ID for ${item.id}: ${item.priceId}`);

    // Validate that the price ID corresponds to the correct product type to prevent mismatching
    if (item.priceId) {
      const productType = item.id;
      const productPrefix = getProductPrefix(productType);

      // Log the product prefix mapping for debugging
      console.log(`Product type ${productType} mapped to prefix ${productPrefix}`);

      // Special log for MRM products
      if (productType === 'modified-registered-mark') {
        console.log(
          `📢 NOTICE: Modified Registered Mark (MRM) is using CMC price IDs as they are the same product type`
        );
      }

      // Extract prefix from price ID (assuming format like price_VMC_1_YEAR)
      const priceIdLower = item.priceId.toLowerCase();

      // Perform validation based on product type
      let isValidPriceId = true;
      if (productType === 'verified-mark-certificate' && !priceIdLower.includes('vmc')) {
        console.error(`⚠️ Invalid price ID for VMC: ${item.priceId}`);
        isValidPriceId = false;
      } else if (productType === 'common-mark-certificate' && !priceIdLower.includes('cmc')) {
        console.error(`⚠️ Invalid price ID for CMC: ${item.priceId}`);
        isValidPriceId = false;
      } else if (productType === 'government-mark-certificate' && !priceIdLower.includes('gmc')) {
        console.error(`⚠️ Invalid price ID for GMC: ${item.priceId}`);
        isValidPriceId = false;
      }

      if (!isValidPriceId) {
        console.log(
          `⚠️ Price ID mismatch detected! Getting correct price ID from environment variables instead.`
        );
        // Continue to other methods rather than returning the invalid price ID
      } else {
        return item.priceId;
      }
    }
  }

  // Check if subscription options are available
  if (item.subscriptionOptions) {
    const period = item.options?.year || 1;
    if (item.subscriptionOptions[period]?.priceId) {
      const priceId = item.subscriptionOptions[period].priceId;

      // Validate the price ID from subscription options
      const productType = item.id;
      const productPrefix = getProductPrefix(productType);
      const priceIdLower = priceId.toLowerCase();

      // Log the product prefix for debugging
      console.log(
        `Using subscription options: Product type ${productType} mapped to prefix ${productPrefix}`
      );

      // Special log for MRM products
      if (productType === 'modified-registered-mark') {
        console.log(
          `📢 NOTICE: Modified Registered Mark (MRM) is using CMC price IDs as they are the same product type with prefix ${productPrefix}`
        );
      }

      let isValidPriceId = true;
      if (productType === 'verified-mark-certificate' && !priceIdLower.includes('vmc')) {
        console.error(`⚠️ Invalid price ID for VMC from subscriptionOptions: ${priceId}`);
        isValidPriceId = false;
      } else if (productType === 'common-mark-certificate' && !priceIdLower.includes('cmc')) {
        console.error(`⚠️ Invalid price ID for CMC from subscriptionOptions: ${priceId}`);
        isValidPriceId = false;
      } else if (productType === 'government-mark-certificate' && !priceIdLower.includes('gmc')) {
        console.error(`⚠️ Invalid price ID for GMC from subscriptionOptions: ${priceId}`);
        isValidPriceId = false;
      }

      if (isValidPriceId) {
        console.log(`Using price ID from subscriptionOptions for ${item.id}: ${priceId}`);
        return priceId;
      } else {
        console.log(
          `⚠️ Invalid price ID in subscriptionOptions! Using environment variables instead.`
        );
      }
    }
  }

  // Get period (defaults to 1 year)
  const period = parseInt(item.options?.year || 1, 10);

  // Ensure the correct product type is used for environment variable lookup
  const normalizedProductType = item.id.toLowerCase().trim();

  // Log the exact product type being used
  console.log(
    `Looking up price ID for product type: ${normalizedProductType} (period: ${period} years)`
  );

  // Get price ID from environment variables using our utility function
  const priceId = getPriceIdFromEnv(normalizedProductType, period);

  if (priceId) {
    // For MRM products, explicitly log that we're using CMC price IDs
    if (normalizedProductType === 'modified-registered-mark') {
      console.log(`📢 Using CMC price ID for MRM product: ${priceId}`);
    } else {
      console.log(
        `Using price ID from environment for ${normalizedProductType}, period ${period}: ${priceId}`
      );
    }
    return priceId;
  }

  // Log detailed error if no price ID found
  console.error(
    `❌ ERROR: No price ID found for subscription product ${normalizedProductType} with period ${period}`
  );
  console.error(`Make sure the environment variables are properly set up`);

  // Last resort: try to construct the environment variable name directly
  const productPrefix = getProductPrefix(normalizedProductType);
  const envVarName = `STRIPE_${productPrefix}_${period}_YEAR_PRICE`;
  console.log(`Trying direct environment variable lookup: ${envVarName}`);

  // List all available price environment variables for debugging
  console.error(
    'Available price environment variables:',
    Object.keys(process.env)
      .filter((k) => k.includes('STRIPE') && k.includes('PRICE'))
      .join(', ')
  );

  return null;
};

/**
 * Determines coupon code based on subscription duration
 * @param {Object} item - Product or cart item
 * @returns {string|null} Coupon code or null
 */
export const getSubscriptionCoupon = (item) => {
  // IMPORTANT: Discounts are now built directly into the price structure in Stripe
  // This function now always returns null as we no longer use separate coupon codes
  // The different subscription durations (1, 2, and 3 years) have their own price IDs
  // with appropriate discounts already applied

  console.log('Coupon request ignored - using built-in price discounts instead of coupons');
  return null;

  // Legacy coupon code logic - removed as of [current date]
  /* 
  if (!isSubscriptionProduct(item)) return null;

  // Get year from item options, default to 1 if not present
  const year = item.options?.year || 1;

  // One year subscription doesn't get a coupon
  if (year === 1) return null;

  // Return appropriate coupon code
  if (year === 2) {
    if (!process.env.STRIPE_COUPON_2YR) {
      console.error('Missing environment variable: STRIPE_COUPON_2YR');
      return null;
    }
    return process.env.STRIPE_COUPON_2YR;
  } else if (year === 3) {
    if (!process.env.STRIPE_COUPON_3YR) {
      console.error('Missing environment variable: STRIPE_COUPON_3YR');
      return null;
    }
    return process.env.STRIPE_COUPON_3YR;
  }

  return null;
  */
};

/**
 * Prepares a cart item for subscription processing
 * @param {Object} item - Cart item
 * @returns {Object} Prepared item with subscription flags
 */
export const prepareCartItemForCheckout = (item) => {
  if (!item) {
    console.error('Cannot prepare undefined or null cart item');
    return null;
  }

  // Ensure we have an ID
  const id = item.id || `unknown-${Date.now()}`;

  // Check if this is a subscription product and whether we need to handle period pricing
  const isSubscription = isSubscriptionProduct(item);

  // Get the subscription period (defaults to 1 year)
  const subscriptionPeriod = item.options?.year || 1;

  // Get logo and domain counts (default to 1 if not specified)
  const logoCount = item.options?.logo || 1;
  const domainCount = item.options?.domain || 1;

  // Generate a consistent certId if one doesn't exist
  const certId = item.certId || generateStandardCertId(id);

  // Enhanced logging for initial cart item state
  console.log(`🔍 CHECKOUT PREPARATION - Cart item ${id}`, {
    id,
    certId,
    isSubscription,
    subscriptionPeriod,
    logoCount,
    domainCount,
    hasOptions: !!item.options,
    hasSubscriptionOptions: !!(
      item.subscriptionOptions &&
      (item.subscriptionOptions[subscriptionPeriod] ||
        item.subscriptionOptions[`${subscriptionPeriod}yr`])
    ),
    price: item.price,
    initialPriceId: item.priceId,
    initialProductId: item.productId,
  });

  // Extract only safe properties for options
  const safeOptions = {};
  if (item.options) {
    // Only copy primitive values to avoid circular references
    if (item.options.logo) safeOptions.logo = Number(item.options.logo);
    if (item.options.domain) safeOptions.domain = Number(item.options.domain);
    if (item.options.year) safeOptions.year = Number(item.options.year);
  }

  // Ensure price exists
  const price = typeof item.price === 'number' ? item.price : 0;

  // IMPORTANT: Make sure we have a product name
  const name =
    item.name ||
    (id === 'government-mark-certificate' ? 'Government Mark Certificate' : 'Certificate');

  // First try to use direct price/product IDs from the item
  let priceId = item.priceId;
  let productId = item.productId;

  console.log(`Initial price/product IDs for ${id}:`, { priceId, productId });

  // Validate and identify the product type
  const productType = id.toLowerCase().trim();
  const isVMC = productType === 'verified-mark-certificate';
  const isGMC = productType === 'government-mark-certificate';
  const isCMC = productType === 'common-mark-certificate';
  const isMRM = productType === 'modified-registered-mark';

  // Log exact product type being processed
  console.log(`Product type identification:`, {
    exactId: id,
    normalizedType: productType,
    isVMC,
    isGMC,
    isCMC,
    isMRM,
  });

  // If no direct IDs and we have subscriptionOptions in the product, try to get from there
  if ((!priceId || !productId) && isSubscription && item.subscriptionOptions) {
    // First try with numeric period key
    if (item.subscriptionOptions[subscriptionPeriod]) {
      const subscriptionOption = item.subscriptionOptions[subscriptionPeriod];

      // Log the full subscription option for debugging
      console.log(
        `Found subscription option with numeric key ${subscriptionPeriod} for ${id}:`,
        JSON.stringify(subscriptionOption, null, 2)
      );

      if (!priceId && subscriptionOption.priceId) {
        priceId = subscriptionOption.priceId;
        console.log(`Set priceId from subscriptionOptions[${subscriptionPeriod}]: ${priceId}`);
      }

      if (!productId && subscriptionOption.productId) {
        productId = subscriptionOption.productId;
        console.log(`Set productId from subscriptionOptions[${subscriptionPeriod}]: ${productId}`);
      }
    }
    // Then try with string period key (e.g. '1yr')
    else if (item.subscriptionOptions[`${subscriptionPeriod}yr`]) {
      const subscriptionOption = item.subscriptionOptions[`${subscriptionPeriod}yr`];

      // Log the full subscription option for debugging
      console.log(
        `Found subscription option with string key ${subscriptionPeriod}yr for ${id}:`,
        JSON.stringify(subscriptionOption, null, 2)
      );

      if (!priceId && subscriptionOption.priceId) {
        priceId = subscriptionOption.priceId;
        console.log(`Set priceId from subscriptionOptions[${subscriptionPeriod}yr]: ${priceId}`);
      }

      if (!productId && subscriptionOption.productId) {
        productId = subscriptionOption.productId;
        console.log(
          `Set productId from subscriptionOptions[${subscriptionPeriod}yr]: ${productId}`
        );
      }
    } else {
      console.warn(
        `⚠️ No matching subscription option found for ${id} with period ${subscriptionPeriod}. Available keys:`,
        Object.keys(item.subscriptionOptions)
      );
    }
  }

  // If still no price ID, try to get from environment variables via getSubscriptionPriceId
  if (!priceId && isSubscription) {
    priceId = getSubscriptionPriceId({
      ...item,
      options: { ...safeOptions },
    });

    console.log(`Got price ID from getSubscriptionPriceId for ${id}: ${priceId}`);
  }

  // If still no product ID, try to get from environment variables
  if (!productId && isSubscription) {
    console.log(`Attempting to get productId from environment variables for ${id}`);

    // Construct product ID for Stripe metadata if applicable
    if (isVMC) {
      const productIdEnvVar = `STRIPE_VMC_${subscriptionPeriod}_YEAR_PRODUCT`;
      productId = process.env[productIdEnvVar];
      console.log(`Looking up VMC product ID from ${productIdEnvVar}: ${productId || 'NOT FOUND'}`);
    } else if (isGMC) {
      const productIdEnvVar = `STRIPE_GMC_${subscriptionPeriod}_YEAR_PRODUCT`;
      productId = process.env[productIdEnvVar];
      console.log(`Looking up GMC product ID from ${productIdEnvVar}: ${productId || 'NOT FOUND'}`);
    } else if (isCMC) {
      const productIdEnvVar = `STRIPE_CMC_${subscriptionPeriod}_YEAR_PRODUCT`;
      productId = process.env[productIdEnvVar];
      console.log(`Looking up CMC product ID from ${productIdEnvVar}: ${productId || 'NOT FOUND'}`);
    } else {
      console.log(`No environment variable pattern for product ID of type: ${id}`);
    }

    console.log(`Got product ID from environment variables for ${id}: ${productId || 'NULL'}`);
  }

  // FIX FOR VMC/CMC PRICE MISMATCH:
  // Validate that the price ID corresponds to the correct product type to prevent mismatching
  if (priceId) {
    const priceIdLower = priceId.toLowerCase();
    let isValidPriceId = true;

    // Check for mismatches between product type and price ID
    if (isVMC && !priceIdLower.includes('vmc')) {
      console.error(`⚠️ Price ID validation failed for VMC: ${priceId} does not contain 'vmc'`);
      isValidPriceId = false;
    } else if (isCMC && !priceIdLower.includes('cmc')) {
      console.error(`⚠️ Price ID validation failed for CMC: ${priceId} does not contain 'cmc'`);
      isValidPriceId = false;
    } else if (isGMC && !priceIdLower.includes('gmc')) {
      console.error(`⚠️ Price ID validation failed for GMC: ${priceId} does not contain 'gmc'`);
      isValidPriceId = false;
    }

    // If price ID doesn't match product type, get correct price ID from env vars
    if (!isValidPriceId) {
      console.log(`⚠️ Price ID mismatch detected! Attempting to get correct price ID for ${id}`);

      // Use proper environment variable for this product type
      const productPrefix = isVMC ? 'VMC' : isCMC ? 'CMC' : isGMC ? 'GMC' : 'MRM';
      const envVarName = `STRIPE_${productPrefix}_${subscriptionPeriod}_YEAR_PRICE`;

      console.log(`Looking up correct price ID from ${envVarName}`);
      const correctPriceId = process.env[envVarName];

      if (correctPriceId) {
        console.log(`✅ Found correct price ID from environment: ${correctPriceId}`);
        priceId = correctPriceId;
      } else {
        console.error(`❌ Could not find correct price ID in environment variables for ${id}`);

        // Log all available price variables for debugging
        console.log(
          'Available price environment variables:',
          Object.keys(process.env)
            .filter((k) => k.includes('STRIPE') && k.includes('PRICE'))
            .join(', ')
        );
      }
    }
  }

  // Final check for missing but required IDs
  if (isSubscription && !priceId) {
    console.error(
      `❌ ERROR: No price ID found for subscription item ${id} with period ${subscriptionPeriod}`
    );
  }

  // Create a safe item for the checkout process
  const safeItem = {
    id,
    name,
    isSubscription,
    certId,
    options: safeOptions,
    priceId,
    productId,
    price,
  };

  // Log final prepared item to help with debugging
  console.log(`✅ Final prepared cart item for ${id}:`, {
    id: safeItem.id,
    name: safeItem.name,
    certId: safeItem.certId,
    isSubscription: safeItem.isSubscription,
    period: safeItem.options?.year || 1,
    priceId: safeItem.priceId || 'MISSING',
    productId: safeItem.productId || 'MISSING',
    price: safeItem.price,
    productType: isVMC ? 'VMC' : isCMC ? 'CMC' : isGMC ? 'GMC' : 'Other',
  });

  return safeItem;
};
